import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import classNames from 'classnames';
import { I13nButton, I13nAnchor } from 'react-i13n';

import I13nLink from './I13nLink';

// These are props we will be using to detemine characteristics of the button
// and should not therefore not be passed to through to the child.
// We will omit these before passing all other props through to child.
const BLACKLIST_TO_CHILDREN_PROPS = [
  'type',
  'selected',
  'size',
  'disabled',
  'className',
  'loading',
  'i13nModel',
  'i13nLabel',
  'testid',
  'newTab',
  'target',
  'rel',
  'accessDenied',
  'nativeButtonType',
  'showOnAccessDenied',
];

class Button extends PureComponent {
  static propTypes = {
    /**
     * Only required Button prop. Will be prefixed with 'btn-' when applied.
     */
    testid: PropTypes.string.isRequired,
    /**
     * Options: 'standard' (default), 'medium', 'small', 'no-miw' (to apply only
     * styling that there should be no min width set for the button).
     * Applies a classname corresponding to the size.
     */
    size: PropTypes.oneOf(['standard', 'medium', 'small', 'no-miw']),
    /**
     * Options: 'primary' (default), 'secondary', 'tertiary', 'dashed', 'danger'.
     * Applies a classname corresponding to the type. If the
     * 'disabled' prop is true AND the button renders to an <a> tag,
     * the type__disabled class is applied INSTEAD of the regular type class.
     */
    type: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'dashed', 'danger']),
    /**
     * If passed in, button will represent an external link and be rendered as
     * an <a> tag. The 'newTab' prop is only relevant in this scenario.
     */
    href: PropTypes.string,
    /**
     * When 'href' prop is passed in and 'newTab' prop is true, will apply
     * target="_blank", as well as rel="noopener noreferrer" for security.
     */
    newTab: PropTypes.bool,
    /**
     * If passed in, button will represent an internal link and be rendered
     * ultimately as an <a> tag. Utilizes 'react-router's 'Link' component.
     */
    to: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        pathname: PropTypes.string,
        query: PropTypes.object,
        hash: PropTypes.string,
      }),
    ]),
    /**
     * If button renders to a <button> tag (has no 'href' or 'to' property),
     * applies disabled state. If renders to an <a> tag, passing 'disabled' prop
     * as true will mean the corresponding type__disabled class is applied
     * instead of regular type class. The same styling is applied in either the
     * <button> or <a> scenario.
     */
    disabled: PropTypes.bool,
    /**
     * Applies a `{type}-selected` classname. Currently there only existing
     * selected styling for the 'secondary' type (is benign for other types).
     * Used for example in BinaryToggle component, where 'Yes' and 'No' buttons
     * are present and the user is selecting one or the other.
     */
    selected: PropTypes.bool,
    /**
     * Will be applied as the label field on the i13nModel if passed in, along
     * with default of action: 'click'. If neither i13nLabel or i13nModel are
     * passed in AND the child of the Button is text, label will default to
     * this text (most common scenario). Scenarios where you may not want this is
     * when the Button text is too generic - i.e. 'Disable' when there are many
     * 'Disable' buttons on the page. The label is always automatically prefixed
     * with what page of the dashboard the user is on so this information does
     * not need to be included in the label.
     */
    i13nLabel: PropTypes.string,
    /**
     * Any properties passed on the i13nModel prop will overwrite the
     * corresponding default property: action of 'click',
     * and label set to the text on the Button (if it is text).
     * For example,
     * if the button text is 'Cancel' but you wanted to overwrite with
     * 'Specific Row - Cancel', you could either pass
     * i13nLabel="Specific Row - Cancel"
     * or i13nModel={{ label: 'Specific Row - Cancel' }}. The action: 'click'
     * field will still be applied in the final i13nModel in either scenario.
     * Order of label precendence:
     * 1. i13nLabel
     * 2. i13nModel.label
     * 3. If the Button child is text, we will default the label to this text.
     * The third option will be the most common scenario.
     */
    i13nModel: PropTypes.object,
    /**
     * Shows loading spinner within button and disables button
     */
    loading: PropTypes.bool,

    /**
     * Should button be disabled because access is denied?
     * If true a lock icon is added on the button along with a message on hover
     */
    accessDenied: PropTypes.bool,

    /**
     * Passes this as a type to the native HTML element only if it doesn't have
     * `to` or `href`
     * @type {String}
     */
    nativeButtonType: PropTypes.string,
    /**
     * custom text or component to show instead of regular this.props.children + lock when access is denied
     */
    showOnAccessDenied: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    /**
     * Any other props are passed straight through to the child
     */
  };

  static defaultProps = {
    type: 'primary',
    selected: false,
    size: 'standard',
    disabled: false,
    loading: false,
    newTab: true,
    nativeButtonType: 'button',
  };

  render() {
    const { type, selected, size, loading, onClick, accessDenied, nativeButtonType, showOnAccessDenied } = this.props;

    const accessDeniedLock = (
      <i
        className={classNames('material-icons h_mstart-5', {
          [`button-font-size-${size}`]: !!size,
        })}
      >
        lock
      </i>
    );

    const children = accessDenied ? (
      showOnAccessDenied ? (
        <div>{showOnAccessDenied}</div>
      ) : (
        <div className="access-denied-wrapper">
          {this.props.children}
          {accessDeniedLock}
        </div>
      )
    ) : (
      this.props.children
    );

    // button should be disable when loading also
    const disabled = accessDenied || this.props.disabled || this.props.loading;

    // as <a> tags do not have a disabled property, we need to apply as a class
    const willRenderToDisabledAnchorTag = (this.props.href || this.props.to) && disabled;

    const className = classNames('button', {
      [`button-type-${type}`]: !willRenderToDisabledAnchorTag,
      [`button-type-${type}__disabled`]: willRenderToDisabledAnchorTag,
      [`${type}-selected`]: selected,
      [`button-size-${size}`]: !!size,
      [this.props.className]: !!this.props.className,
      button__loading: !!loading,
      'btn-icon-with-text': accessDenied,
    });

    /**
     * Order of label precendence:
     * 1. Will be i13nLabel if it is passed in.
     * 2. Will be set to i13nModel.label if passed in.
     * 3. If the Button child is text, we will default the label to this text.
     * The third option of defaulting to the Button text will be the most
     * common scenario.
     */
    const i13nModel = R.merge({ action: 'click' }, this.props.i13nModel);

    if (this.props.i13nLabel) {
      i13nModel.label = this.props.i13nLabel;
    }

    if (!i13nModel.label) {
      const childIsText = typeof children === 'string';
      i13nModel.label = childIsText ? children : 'Button';
    }

    const childProps = {
      disabled,
      className,
      i13nModel,
      'data-testid': `btn-${this.props.testid}`,
      ...R.omit(BLACKLIST_TO_CHILDREN_PROPS, { ...this.props }),
      ...{ onClick: disabled ? null : onClick },
    };

    // external link
    if (this.props.href) {
      return (
        <I13nAnchor
          {...childProps}
          target={this.props.newTab ? '_blank' : ''}
          rel={this.props.newTab ? 'noopener noreferrer' : ''}
        >
          {children}
        </I13nAnchor>
      );
    }

    // internal link route
    if (this.props.to) {
      return <I13nLink {...childProps}>{children}</I13nLink>;
    }

    // button triggering a function only (no routing change)
    return (
      <I13nButton type={nativeButtonType} {...childProps}>
        {children}
      </I13nButton>
    );
  }
}

export default Button;
